import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "@helpers/translate";
import { removeUserSession } from "@helpers/axios/private.axios";
import "./UserProfileDropdown.sass";
import UserAccountSummary from "./UserAccountSummary";
import { useDispatch, useSelector } from "react-redux";
import { NAV_ITEMS_DROPDOWN } from "@constants";
import { NavLink } from "react-router-dom";
import {
  LogoutIcon,
  ProfileIcon,
  FinanceIcon,
  SupportIcon,
  TradingIcon,
  WithdrawalIcon,
  SettingsIcon,
  MarketsIcon,
} from "@components/library";
import BlockIcon from "@assets/icons/BlockIcon";
import LanguageDropdown from "@components/layout/Header/components/LanguageDropdown";
import { getTodayStatistics } from "@actions";

const UserProfileDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const dispatch = useDispatch();
  const { __ } = useTranslation();

  const { active_wallet } = useSelector((state) => state.wallet || {});
  const isDemoWalletActive = active_wallet.type === "demo_futures";

  const { today } = useSelector((state) => state.statistics || {});

  const desktopIconByKey = {
    profile: <ProfileIcon />,
    trading: <TradingIcon />,
    deposit: <FinanceIcon />,
    withdrawal: <WithdrawalIcon />,
    support: <SupportIcon />,
    settings: <SettingsIcon />,
    markets: <MarketsIcon />,
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener(
        "mousedown",
        handleClickOutside,
      );
    }

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside,
      );
    };
  }, [isOpen]);

  const handleLogout = () => {
    removeUserSession();
  };

  const openSupportChat = () => {
    document
      .getElementById("sendpulse-chat-wrapper")
      ?.classList.add("active");
  };

  useEffect(() => {
    dispatch(getTodayStatistics());
  }, [dispatch, isDemoWalletActive]);

  return (
    <div
      className="user-profile-dropdown"
      ref={dropdownRef}
    >
      <button
        type="button"
        className="header__mini-btn"
        onClick={() => setIsOpen(!isOpen)}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M15 15.75V14.25C15 13.4544 14.6839 12.6913 14.1213 12.1287C13.5587 11.5661 12.7956 11.25 12 11.25H6C5.20435 11.25 4.44129 11.5661 3.87868 12.1287C3.31607 12.6913 3 13.4544 3 14.25V15.75"
            stroke="white"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9 8.25C10.6569 8.25 12 6.90685 12 5.25C12 3.59315 10.6569 2.25 9 2.25C7.34315 2.25 6 3.59315 6 5.25C6 6.90685 7.34315 8.25 9 8.25Z"
            stroke="white"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="user-profile-dropdown__menu">
          <div className="user-profile-dropdown__left">
            <UserAccountSummary />

            <div className="user-profile-dropdown__btn user-profile-dropdown__btn--orange">
              {isDemoWalletActive
                ? __("trading.demo_account_title")
                : __("trading.real_account_title")}
            </div>

            <div className="user-profile-dropdown__statistics">
              <div className="user-profile-dropdown__title">
                <p>
                  {isDemoWalletActive
                    ? __(
                        "trading.demo_account_today_stats",
                      )
                    : __(
                        "trading.real_account_today_stats",
                      )}
                </p>

                <button
                  type="button"
                  className="user-profile-dropdown__reload-btn"
                  onClick={() =>
                    dispatch(getTodayStatistics())
                  }
                >
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 13 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12.9574 7.2207C12.5987 10.5154 9.82267 13 6.5 13C4.23533 13 2.16933 11.8194 1 9.95736V11.8333C1 12.1093 0.776 12.3333 0.5 12.3333C0.224 12.3333 0 12.1093 0 11.8333V8.5C0 8.224 0.224 8 0.5 8H3.83333C4.10933 8 4.33333 8.224 4.33333 8.5C4.33333 8.776 4.10933 9 3.83333 9H1.61328C2.53995 10.8173 4.418 12 6.5 12C9.31133 12 11.6594 9.89863 11.9634 7.11263C11.9927 6.8373 12.242 6.6406 12.514 6.66927C12.7893 6.69927 12.9867 6.94604 12.9574 7.2207ZM12.5 0.666667C12.224 0.666667 12 0.890667 12 1.16667V3.04264C10.8307 1.18064 8.76467 0 6.5 0C3.178 0 0.401977 2.48463 0.0426432 5.7793C0.0126432 6.05396 0.210685 6.30073 0.485352 6.33073C0.503352 6.33273 0.522039 6.33333 0.540039 6.33333C0.792039 6.33333 1.00864 6.14337 1.03597 5.88737C1.33997 3.10137 3.68868 1 6.49935 1C8.58202 1 10.4594 2.18267 11.3861 4H9.16667C8.89067 4 8.66667 4.224 8.66667 4.5C8.66667 4.776 8.89067 5 9.16667 5H12.5C12.776 5 13 4.776 13 4.5V1.16667C13 0.890667 12.776 0.666667 12.5 0.666667Z"
                      fill="#888888"
                    />
                  </svg>
                </button>
              </div>

              <div className="user-profile-dropdown__stats">
                <div className="user-profile-dropdown__stats-row">
                  <span>
                    {__(
                      "trading.real_account_trades",
                    )}
                  </span>
                  <span>{today?.trades ?? 0}</span>
                </div>

                <div className="user-profile-dropdown__stats-row">
                  <span>
                    {__(
                      "trading.real_account_turnover",
                    )}
                  </span>
                  <span>
                    {today?.trading_turnover ?? 0}
                  </span>
                </div>

                <div className="user-profile-dropdown__stats-row">
                  <span>
                    {__(
                      "trading.real_account_profit",
                    )}
                  </span>
                  <span>
                    {today?.trading_profit ?? 0} USDT
                  </span>
                </div>
              </div>

              <div className="user-profile-dropdown__btn user-profile-dropdown__btn--green">
                {isDemoWalletActive
                  ? __(
                      "trading.demo_account_overall_stats",
                    )
                  : __(
                      "trading.real_account_overall_stats",
                    )}
              </div>
            </div>
          </div>

          <nav className="user-profile-dropdown__right">
            {NAV_ITEMS_DROPDOWN.map((item) => {
              const {
                key,
                to,
                textKey,
                isDisabled,
                icon,
                isSidebarAction,
              } = item;

              if (isSidebarAction) {
                return (
                  <li
                    key={key}
                    className="mobile-sidebar__item"
                  >
                    <button
                      type="button"
                      className="mobile-sidebar__nav-link"
                      onClick={() => {
                        if (isDisabled) return;

                        setIsOpen(false);

                        if (
                          key === "support_mobile"
                        ) {
                          openSupportChat();
                        }
                      }}
                      aria-disabled={
                        isDisabled ? "true" : "false"
                      }
                      tabIndex={isDisabled ? -1 : 0}
                    >
                      <span
                        className={`mobile-sidebar__nav-icon${
                          isDisabled
                            ? " mobile-sidebar__nav-icon--disabled"
                            : ""
                        }`}
                      >
                        {desktopIconByKey[icon]}
                      </span>

                      <p
                        className={`mobile-sidebar__nav-text${
                          isDisabled
                            ? " mobile-sidebar__nav-text--disabled"
                            : ""
                        }`}
                      >
                        {__(textKey)}
                      </p>

                      {isDisabled && (
                        <div className="mobile-sidebar__disabled">
                          <BlockIcon />

                          <span className="mobile-sidebar__disabled-text">
                            {__(
                              "common.coming-soon",
                            )}
                          </span>
                        </div>
                      )}
                    </button>
                  </li>
                );
              }

              return (
                <li
                  key={key}
                  className="mobile-sidebar__item"
                >
                  <NavLink
                    end={key === "my_profile"}
                    onClick={(e) => {
                      if (isDisabled) {
                        e.preventDefault();
                        e.stopPropagation();
                        return;
                      }

                      setIsOpen(false);
                    }}
                    to={to}
                    aria-disabled={
                      isDisabled ? "true" : "false"
                    }
                    tabIndex={isDisabled ? -1 : 0}
                    className={({ isActive }) =>
                      `mobile-sidebar__nav-link${
                        isActive
                          ? " active"
                          : ""
                      }`
                    }
                  >
                    <span
                      className={`mobile-sidebar__nav-icon${
                        isDisabled
                          ? " mobile-sidebar__nav-icon--disabled"
                          : ""
                      }`}
                    >
                      {desktopIconByKey[icon]}
                    </span>

                    <p
                      className={`mobile-sidebar__nav-text${
                        isDisabled
                          ? " mobile-sidebar__nav-text--disabled"
                          : ""
                      }`}
                    >
                      {__(textKey)}
                    </p>

                    {isDisabled && (
                      <div className="mobile-sidebar__disabled">
                        <BlockIcon />

                        <span className="mobile-sidebar__disabled-text">
                          {__(
                            "common.coming-soon",
                          )}
                        </span>
                      </div>
                    )}
                  </NavLink>
                </li>
              );
            })}

            <div className="user-profile-dropdown__lang-wrap">
              <LanguageDropdown variant="profile" />
            </div>

            <div
              className="user-profile-dropdown__item user-profile-dropdown__item--logout mobile-sidebar__nav-link"
              onClick={handleLogout}
            >
              <LogoutIcon className="mobile-sidebar__nav-icon mobile-sidebar__nav-icon--logout" />

              <span className="mobile-sidebar__nav-text">
                {__("common.logout") || "Log out"}
              </span>
            </div>
          </nav>
        </div>
      )}
    </div>
  );
};

export default UserProfileDropdown;